// Drawer — five tabs, opens against the right side of the board for the
// currently selected run. Replaces the prototype's `drawer.jsx`.

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import * as api from '../lib/api';
import { ApiError } from '../lib/api';
import { eventToLast } from '../lib/runMapping';
import { useRunEventStream } from '../hooks/useRunEventStream';
import { useConfig } from '../hooks/useConfig';
import type { ApiEventLine, RunVM } from '../lib/types';

export type DrawerAction = 'cancel' | 'escalate' | 'resume';

interface DrawerProps {
  run: RunVM;
  onClose: () => void;
  onAction: (action: DrawerAction, runId: string) => void;
}

type TabId = 'overview' | 'chat' | 'events' | 'diff' | 'reviews' | 'cost';

interface TimedEvent {
  ts: string;
  type: string;
  text: string;
  // raw line for the cost / reviews calculations
  raw: ApiEventLine;
}

function formatTs(timestamp?: string | undefined): string {
  if (!timestamp) return 'now';
  const d = new Date(timestamp);
  if (Number.isNaN(d.getTime())) return 'now';
  return [d.getHours(), d.getMinutes(), d.getSeconds()]
    .map((n) => String(n).padStart(2, '0'))
    .join(':');
}

function lineToTimed(line: ApiEventLine): TimedEvent {
  const last = eventToLast(line);
  return {
    ts: formatTs(typeof line.timestamp === 'string' ? line.timestamp : undefined),
    type: last.type,
    text: last.text,
    raw: line,
  };
}

export function Drawer({ run, onClose, onAction }: DrawerProps) {
  const [tab, setTab] = useState<TabId>('overview');
  const [muted, setMuted] = useState<Set<string>>(new Set());
  const [attachOpen, setAttachOpen] = useState(false);
  // Historical events drained on open — newest first to match liveEvents.
  // Despite the name, this is the FULL event timeline for the run, not just
  // the last N. Loaded via /runs/:id/events?follow=false because /runs/:id's
  // `recent_events` is capped at 10 in idx-ASC order — useless for runs
  // whose chat content lives past event #10 (i.e. nearly every real run).
  const [recentEvents, setRecentEvents] = useState<TimedEvent[]>([]);
  const [historyMaxIdx, setHistoryMaxIdx] = useState<number>(-1);
  const [liveEvents, setLiveEvents] = useState<TimedEvent[]>([]);
  const [streamConnected, setStreamConnected] = useState(false);

  // Reset state on run switch.
  useEffect(() => {
    setTab('overview');
    setMuted(new Set());
    setLiveEvents([]);
    setRecentEvents([]);
    setHistoryMaxIdx(-1);
    setStreamConnected(false);
  }, [run.runId]);

  // Initial historical fetch — drains the full event timeline so the chat /
  // reviews / cost tabs have real data even on terminal runs where the live
  // stream is disabled.
  useEffect(() => {
    const ctrl = new AbortController();
    void (async () => {
      try {
        const events = await api.fetchAllEvents(run.runId, ctrl.signal);
        if (ctrl.signal.aborted) return;
        // fetchAllEvents returns idx-ASC; reverse to newest-first so the
        // events tab's [...liveEvents, ...recentEvents] read stays time-ordered.
        const timed = events.map(lineToTimed);
        let maxIdx = -1;
        for (const e of events) {
          if (typeof e.idx === 'number' && e.idx > maxIdx) maxIdx = e.idx;
        }
        setRecentEvents(timed.reverse());
        setHistoryMaxIdx(maxIdx);
      } catch {
        // best-effort — leave history empty
      }
    })();
    return () => { ctrl.abort(); };
  }, [run.runId]);

  // Live tail — only when the run is non-terminal. Seed the stream's
  // after_idx with the last idx we drained from history so the server
  // doesn't replay events we already have. Note: initialAfterIdx is read
  // once at hook-mount; we pass historyMaxIdx after the drain settles by
  // re-mounting on runId change.
  const isTerminal = ['succeeded', 'failed', 'escalated', 'cancelled'].includes(run.status);
  useRunEventStream({
    enabled: !isTerminal,
    runId: run.runId,
    initialAfterIdx: historyMaxIdx >= 0 ? historyMaxIdx : undefined,
    onEvent: (line) => {
      setStreamConnected(true);
      setLiveEvents((prev) => [lineToTimed(line), ...prev].slice(0, 200));
    },
    onTerminal: () => {
      setStreamConnected(false);
    },
  });

  const allEvents = useMemo<TimedEvent[]>(() => {
    // live (newest first) + history (also newest first after .reverse()). A
    // brief overlap window is possible if the live stream connects before
    // initialAfterIdx settles, so dedupe by idx.
    const seen = new Set<number>();
    const out: TimedEvent[] = [];
    for (const e of [...liveEvents, ...recentEvents]) {
      const idx = typeof e.raw.idx === 'number' ? e.raw.idx : -1;
      if (idx >= 0) {
        if (seen.has(idx)) continue;
        seen.add(idx);
      }
      out.push(e);
    }
    return out;
  }, [liveEvents, recentEvents]);

  const eventTypes = useMemo(() => {
    const set = new Set<string>();
    for (const e of allEvents) set.add(e.type);
    return Array.from(set);
  }, [allEvents]);

  const filteredEvents = useMemo(
    () => allEvents.filter((e) => !muted.has(e.type)),
    [allEvents, muted],
  );

  const toggleMute = (t: string): void => {
    setMuted((prev) => {
      const next = new Set(prev);
      if (next.has(t)) next.delete(t); else next.add(t);
      return next;
    });
  };

  return (
    <div className="drawer">
      <div className="drawer-head">
        <div className="drawer-head-left">
          <span className="ticket-id">[{run.id}]</span>
          <span className="drawer-title">{run.title}</span>
        </div>
        <button className="icon-btn" onClick={onClose} aria-label="close">×</button>
      </div>

      <div className="drawer-tabs">
        {(['overview', 'chat', 'events', 'diff', 'reviews', 'cost'] as TabId[]).map((t) => (
          <button
            key={t}
            className={`drawer-tab ${tab === t ? 'active' : ''}`}
            onClick={() => setTab(t)}
          >
            {t}
          </button>
        ))}
      </div>

      <div className="drawer-body">
        {tab === 'overview' && <OverviewTab run={run} />}
        {tab === 'chat' && (
          <ChatTab
            events={allEvents}
            connected={streamConnected}
            isTerminal={isTerminal}
            runId={run.runId}
            canInterject={isPiManagedRun(run) && !isTerminal}
          />
        )}
        {tab === 'events' && (
          <EventsTab
            events={filteredEvents}
            types={eventTypes}
            muted={muted}
            onToggleMute={toggleMute}
            connected={streamConnected}
          />
        )}
        {tab === 'diff' && <DiffTab run={run} />}
        {tab === 'reviews' && <ReviewsTab events={allEvents} />}
        {tab === 'cost' && <CostTab run={run} events={allEvents} />}
      </div>

      <div className="drawer-foot">
        {/* Label adapts to status: a parked (awaiting_review) run isn't
            "running" anymore, so "cancel run" reads wrong. The backend's
            POST /runs/:id/cancel branches on status and transitions parked
            runs directly to 'cancelled' — same endpoint, same call site,
            just a clearer button. */}
        <button
          className="foot-btn"
          onClick={() => onAction('cancel', run.runId)}
          disabled={isTerminal}
          title={run.status === 'awaiting_review'
            ? 'Remove this run from the human queue. Marks it cancelled — the PR stays open on GitHub for you to merge or close manually.'
            : 'Cancel the in-flight run. The worker aborts the strategy and the row terminates as cancelled.'}
        >
          {run.status === 'awaiting_review' ? 'remove from queue' : 'cancel run'}
        </button>
        <button className="foot-btn warn" onClick={() => onAction('escalate', run.runId)} disabled={isTerminal}>
          escalate
        </button>
        {/* Attach — drops the operator into the live pi-managed Fly Machine.
            Shown for any run that we know is pi-managed AND has a Machine id
            stamped. The endpoint returns 410 if Fly has already destroyed
            the Machine, and the modal surfaces that without us pre-filtering
            on terminal state (a keepAlive Machine can persist past run end). */}
        {isPiManagedRun(run) && run.sessionId && (
          <button
            className="foot-btn"
            onClick={() => setAttachOpen(true)}
            title="Open ssh + flyctl commands to teleport into this run's Fly Machine"
          >
            attach
          </button>
        )}
        {/* M13: resume — only visible for terminal runs that have a
            session_id stamped on metadata. Uses POST /runs/:id/resume to
            re-queue with the prior session id. */}
        {isTerminal && run.sessionId && (run.status === 'failed' || run.status === 'escalated' || run.status === 'cancelled') && (
          <button
            className="foot-btn"
            onClick={() => onAction('resume', run.runId)}
            title={`Resume from session ${run.sessionId}`}
          >
            resume
          </button>
        )}
      </div>
      {attachOpen && (
        <AttachModal runId={run.runId} onClose={() => setAttachOpen(false)} />
      )}
    </div>
  );
}

// A pi-managed run is identifiable by either the run-level strategy column
// (set by run-ticket on claim) or the session_strategy stamped on metadata
// when the strategy first fired onSession. Both are checked because the
// strategy column is null when the ticket didn't pin one explicitly.
function isPiManagedRun(run: RunVM): boolean {
  return run.strategy === 'pi-managed' || run.sessionStrategy === 'pi-managed';
}

interface AttachModalProps {
  runId: string;
  onClose: () => void;
}

function AttachModal({ runId, onClose }: AttachModalProps) {
  const [state, setState] = useState<
    | { kind: 'loading' }
    | { kind: 'ok'; info: api.AttachInfoResponse['info'] }
    | { kind: 'error'; status: number; message: string }
  >({ kind: 'loading' });

  useEffect(() => {
    const ctrl = new AbortController();
    let tokenIdToRevoke: string | undefined;
    void (async () => {
      try {
        const resp = await api.attachRun(runId, ctrl.signal);
        if (!ctrl.signal.aborted) {
          tokenIdToRevoke = resp.info.token_id;
          setState({ kind: 'ok', info: resp.info });
        }
      } catch (err) {
        if (ctrl.signal.aborted) return;
        if (err instanceof ApiError) {
          setState({ kind: 'error', status: err.status, message: err.message });
        } else {
          setState({ kind: 'error', status: 0, message: err instanceof Error ? err.message : String(err) });
        }
      }
    })();
    return () => {
      ctrl.abort();
      // Kill the scoped Fly token early so we don't leak a live shell
      // credential after the modal closes. Best-effort — token TTL is the
      // safety net if the revoke call fails.
      if (tokenIdToRevoke) {
        void api.revokeAttach(runId, tokenIdToRevoke).catch(() => { /* swallow */ });
      }
    };
  }, [runId]);

  return (
    <div className="attach-modal-backdrop" onClick={onClose}>
      <div className="attach-modal" onClick={(e) => e.stopPropagation()}>
        <div className="attach-modal-head">
          <h3>Teleport into Fly Machine</h3>
          <button className="icon-btn" onClick={onClose} aria-label="close">×</button>
        </div>
        {state.kind === 'loading' && <p className="dim">Resolving Machine…</p>}
        {state.kind === 'error' && (
          <div className="attach-modal-error">
            <p><strong>Cannot attach.</strong> {state.message}</p>
            {state.status === 403 && (
              <p className="dim">
                You're not signed in. Re-authenticate via Cloudflare Access and
                try again.
              </p>
            )}
            {state.status === 404 && (
              <p className="dim">
                This run isn't yours, or doesn't exist. Multi-tenant runs are
                scoped per user — only the operator who fired the run can attach.
              </p>
            )}
            {state.status === 410 && (
              <p className="dim">
                The Fly Machine has already been destroyed — the reaper or
                auto_destroy cleaned it up after the run finished.
              </p>
            )}
            {state.status === 409 && (
              <p className="dim">
                Only pi-managed runs can be attached — other strategies don't
                have a remote shell to drop into.
              </p>
            )}
          </div>
        )}
        {state.kind === 'ok' && <AttachInfoView info={state.info} />}
      </div>
    </div>
  );
}

function AttachInfoView({ info }: { info: api.AttachInfoResponse['info'] }) {
  const scoped = Boolean(info.token_id);
  return (
    <div className="attach-info">
      <dl className="attach-meta">
        <dt>app</dt><dd className="mono">{info.fly_app}</dd>
        <dt>machine</dt><dd className="mono">{info.machine_id}</dd>
        <dt>state</dt><dd className="mono">{info.machine_state}</dd>
        {info.region && (<><dt>region</dt><dd className="mono">{info.region}</dd></>)}
        {info.branch && (<><dt>branch</dt><dd className="mono">{info.branch}</dd></>)}
        {info.keepalive_until && (
          <><dt>keepalive_until</dt><dd className="mono">{info.keepalive_until}</dd></>
        )}
        {info.token_ttl_seconds !== undefined && (
          <><dt>token ttl</dt><dd className="mono">{info.token_ttl_seconds}s</dd></>
        )}
      </dl>
      <h4>Run from your terminal</h4>
      <CommandRow label="ssh" cmd={info.commands.ssh} />
      <CommandRow label="logs" cmd={info.commands.logs} />
      <CommandRow label="proxy" cmd={info.commands.proxy} />
      <h4>Once you're inside</h4>
      <pre className="attach-hints">
{info.shell_hints.join('\n')}
      </pre>
      {scoped ? (
        <p className="dim attach-warn">
          The commands embed a Fly token scoped to this Machine only. It expires
          automatically; closing this modal revokes it immediately.
        </p>
      ) : (
        <p className="dim attach-warn">
          flyctl must be installed locally and authenticated to the Fly org
          that owns <span className="mono">{info.fly_app}</span>.
        </p>
      )}
    </div>
  );
}

function CommandRow({ label, cmd }: { label: string; cmd: string }) {
  const [copied, setCopied] = useState(false);
  const onCopy = (): void => {
    void navigator.clipboard.writeText(cmd).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };
  return (
    <div className="cmd-row">
      <div className="cmd-label">{label}</div>
      <code className="cmd-text">{cmd}</code>
      <button className="cmd-copy" onClick={onCopy} title="copy to clipboard">
        {copied ? 'copied' : 'copy'}
      </button>
    </div>
  );
}

function OverviewTab({ run }: { run: RunVM }) {
  return (
    <div className="ov">
      <section>
        <h4>description</h4>
        <p className="ov-desc">{run.description || <em className="dim">(no description in metadata)</em>}</p>
      </section>
      <section>
        <h4>acceptance criteria</h4>
        {run.ac.length ? (
          <ul className="ac-list">
            {run.ac.map((a, i) => (
              <li key={i} className={a.done ? 'ac-done' : ''}>
                <span className="ac-box">{a.done ? '✓' : '○'}</span>
                <span>{a.text}</span>
              </li>
            ))}
          </ul>
        ) : (
          <div className="ov-warn">no acceptance criteria — add some before starting</div>
        )}
      </section>
      <section className="ov-grid">
        <div>
          <h4>agent config</h4>
          <dl>
            <dt>mode</dt><dd className="mono">{run.mode}</dd>
            <dt>agent</dt><dd className="mono">{run.agent}</dd>
            <dt>reviewer</dt><dd className="mono">{run.reviewer}</dd>
            <dt>loop max</dt><dd className="mono">{run.loop.max}</dd>
            <dt>strategy</dt><dd className="mono">{run.strategy ?? '—'}</dd>
          </dl>
        </div>
        <div>
          <h4>execution context</h4>
          <dl>
            <dt>{run.local ? 'repo' : 'sandbox'}</dt>
            <dd className="mono">{run.local ? '(local)' : run.sandbox ?? '—'}</dd>
            <dt>branch</dt>
            <dd className="mono">
              {run.branch ?? (
                run.targetBranch && run.targetBranchUrl ? (
                  <a href={run.targetBranchUrl} target="_blank" rel="noreferrer noopener" title="agent will push here">
                    {run.targetBranch} ↗
                  </a>
                ) : '—'
              )}
            </dd>
            {run.sessionId && (
              <>
                <dt>session</dt>
                <dd className="mono" title={run.sessionId}>
                  {run.sessionId.slice(0, 12)}…
                </dd>
              </>
            )}
            <dt>source</dt><dd className="mono">{run.source}</dd>
            <dt>status</dt><dd className="mono">{run.status}</dd>
          </dl>
        </div>
      </section>
    </div>
  );
}

// ChatTab — renders the agent's per-turn narration (text_preview) as
// chat bubbles. Pulls from pi_managed_turn / pi_agent_turn /
// claude_agent_sdk_turn events, which share a stable shape:
//   tools: string[], tool_count, text_preview, turn, tokens_in/out,
//   cost_usd_turn_estimate, cost_usd_so_far_estimate.
//
// Turns without a text_preview still render (showing only the tool chips +
// turn number) so the user can see "the agent worked, just didn't narrate".
// When the run is active AND on the pi-managed strategy, the composer at
// the bottom POSTs to /runs/:id/interject — queueing a user message that
// the pi-runner appends as a new turn at the next session.prompt() boundary.
// Other strategies leave the composer disabled with a clear reason.

interface TurnBubble {
  turn: number;
  tools: string[];
  textPreview: string;
  costSoFar: number | null;
  ts: string;
  model: string | null;
}

// Mid-turn liveness — find the most recent pi_managed_tool_start that hasn't
// been paired with a pi_managed_tool_end yet. That's the tool currently
// running on the agentic-compute VM. Returns null when nothing is active.
// Without this, a long `bash $ npm install` looked like a 5-minute stall.
interface ActiveTool {
  toolName: string;
  argsPreview: string;
  startedAt: string;
  turn: number;
}

function extractActiveTool(events: TimedEvent[]): ActiveTool | null {
  const endedCallIds = new Set<string>();
  // Walk newest → oldest so we hit the latest start first, but collect ends
  // in a single pass. Both pi-managed and claude-managed emit tool_start /
  // tool_end with the same tool_call_id shape; treat them as one stream so
  // the "agent is using X" badge works for either strategy.
  for (const e of events) {
    const name = e.raw['event'];
    if (name === 'pi_managed_tool_end' || name === 'claude_managed_tool_end') {
      const id = typeof e.raw['tool_call_id'] === 'string' ? e.raw['tool_call_id'] : null;
      if (id) endedCallIds.add(id);
    }
  }
  for (const e of events) {
    const name = e.raw['event'];
    if (name !== 'pi_managed_tool_start' && name !== 'claude_managed_tool_start') continue;
    const id = typeof e.raw['tool_call_id'] === 'string' ? e.raw['tool_call_id'] : null;
    if (!id || endedCallIds.has(id)) continue;
    return {
      toolName: typeof e.raw['tool_name'] === 'string' ? e.raw['tool_name'] : 'tool',
      argsPreview: typeof e.raw['args_preview'] === 'string' ? e.raw['args_preview'] : '',
      startedAt: e.ts,
      turn: typeof e.raw['turn'] === 'number' ? (e.raw['turn'] as number) : 0,
    };
  }
  return null;
}

function extractTurnBubbles(events: TimedEvent[]): TurnBubble[] {
  const seen = new Map<number, TurnBubble>();
  // Iterate oldest → newest so a later, more complete event for the same
  // turn (rare, but possible across reconnects) overwrites earlier partials.
  const ordered = [...events].reverse();
  for (const e of ordered) {
    const name = typeof e.raw['event'] === 'string' ? (e.raw['event'] as string) : '';
    if (
      name !== 'pi_managed_turn'
      && name !== 'pi_agent_turn'
      && name !== 'claude_agent_sdk_turn'
      && name !== 'claude_managed_turn'
    ) {
      continue;
    }
    const turn = typeof e.raw['turn'] === 'number' ? (e.raw['turn'] as number) : null;
    if (turn === null) continue;
    const toolsRaw = e.raw['tools'];
    const tools: string[] = Array.isArray(toolsRaw)
      ? toolsRaw.filter((t): t is string => typeof t === 'string' && t.length > 0)
      : [];
    const textPreview = typeof e.raw['text_preview'] === 'string'
      ? (e.raw['text_preview'] as string)
      : '';
    const costSoFar = typeof e.raw['cost_usd_so_far_estimate'] === 'number'
      ? (e.raw['cost_usd_so_far_estimate'] as number)
      : null;
    const model = typeof e.raw['model'] === 'string' ? (e.raw['model'] as string) : null;
    seen.set(turn, { turn, tools, textPreview, costSoFar, ts: e.ts, model });
  }
  return Array.from(seen.values()).sort((a, b) => a.turn - b.turn);
}

// Match the sidecar's MAX_INTERJECTION_BYTES so the form rejects oversize
// messages locally instead of round-tripping for a 413. Kept in sync with
// PI_MANAGED_MAX_INTERJECTION_BYTES on the server.
const INTERJECT_MAX_BYTES = 8 * 1024;

interface QueuedMessageBubble {
  ts: string;
  text: string;
  result: 'queued' | 'failed';
  reason: string | null;
  by: string | null;
}

// Pulls `run_interjection` events out of the timeline so the chat tab can
// show the operator's queued messages inline with the agent's narration.
// Both 'queued' (delivered to the sidecar) and 'failed' (Fly returned an
// error) render — failures surface a small badge so the operator can see
// why their message didn't land.
function extractQueuedBubbles(events: TimedEvent[]): QueuedMessageBubble[] {
  const out: QueuedMessageBubble[] = [];
  // Walk oldest → newest so the timeline reads top-to-bottom.
  const ordered = [...events].reverse();
  for (const e of ordered) {
    if (e.raw['event'] !== 'run_interjection') continue;
    const fields = e.raw['fields'] && typeof e.raw['fields'] === 'object'
      ? (e.raw['fields'] as Record<string, unknown>)
      : e.raw;
    const result = fields['result'] === 'queued' ? 'queued' : 'failed';
    const by = typeof fields['by'] === 'string' ? (fields['by'] as string) : null;
    const reason = typeof fields['reason'] === 'string' ? (fields['reason'] as string) : null;
    // The platform doesn't echo the message text on the event for privacy —
    // it's already in the operator's local form state. Show the byte count
    // as a placeholder so the timeline still acknowledges the send.
    const bytes = typeof fields['bytes'] === 'number' ? (fields['bytes'] as number) : null;
    const text = bytes !== null ? `(${bytes} bytes queued)` : '(queued)';
    out.push({ ts: e.ts, text, result, reason, by });
  }
  return out;
}

function ChatTab(props: {
  events: TimedEvent[];
  connected: boolean;
  isTerminal: boolean;
  runId: string;
  canInterject: boolean;
}) {
  const bubbles = useMemo(() => extractTurnBubbles(props.events), [props.events]);
  const queuedBubbles = useMemo(() => extractQueuedBubbles(props.events), [props.events]);
  const activeTool = useMemo(
    () => (props.isTerminal ? null : extractActiveTool(props.events)),
    [props.events, props.isTerminal],
  );
  const totalCost = bubbles.length > 0
    ? (bubbles[bubbles.length - 1]?.costSoFar ?? null)
    : null;

  const [composeText, setComposeText] = useState('');
  const [composeStatus, setComposeStatus] = useState<
    | { kind: 'idle' }
    | { kind: 'sending' }
    | { kind: 'error'; reason: string }
  >({ kind: 'idle' });
  const trimmedLength = composeText.trim().length;
  const composeBytes = new TextEncoder().encode(composeText.trim()).length;
  const canSubmit =
    props.canInterject &&
    composeStatus.kind !== 'sending' &&
    trimmedLength > 0 &&
    composeBytes <= INTERJECT_MAX_BYTES;

  const onSubmit = useCallback(async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!canSubmit) return;
    const message = composeText.trim();
    setComposeStatus({ kind: 'sending' });
    try {
      await api.interjectRun(props.runId, message);
      setComposeText('');
      setComposeStatus({ kind: 'idle' });
    } catch (err) {
      const reason = err instanceof ApiError
        ? `${err.status}: ${err.message}`
        : (err instanceof Error ? err.message : String(err));
      setComposeStatus({ kind: 'error', reason });
    }
  }, [composeText, canSubmit, props.runId]);

  let composePlaceholder: string;
  if (props.isTerminal) {
    composePlaceholder = 'run is terminal — queue message is unavailable';
  } else if (!props.canInterject) {
    composePlaceholder = 'queue message is only supported for pi-managed runs';
  } else if (composeStatus.kind === 'sending') {
    composePlaceholder = 'queueing…';
  } else {
    composePlaceholder = 'queue a message for the agent (lands at the next turn boundary)';
  }

  return (
    // NOTE: the outer class is `drawer-chat-tab`, NOT `chat-tab`. The
    // ChatPanel's collapse-chip ([aside.chat > div.chat-tab](ChatPanel.tsx))
    // already owns `.chat-tab` with `position: absolute; transform:
    // translate(-100%, -50%)`, which positions any element bearing that class
    // off-screen to the left. A previous version of this component used
    // `chat-tab` here too, so the entire drawer chat panel rendered as an
    // invisible off-screen chip — the tab body was empty in the UI even
    // though bubbles were in the DOM.
    <div className="drawer-chat-tab">
      <div className="chat-header">
        <span className={`conn-dot ${props.connected ? '' : 'disconnected'}`} />
        <span className="chat-header-label mono">
          agent narration · {bubbles.length} turn{bubbles.length === 1 ? '' : 's'}
          {totalCost !== null && (
            <> · est ${totalCost.toFixed(4)}</>
          )}
        </span>
      </div>

      {activeTool && (
        <div className="chat-active-tool mono">
          <span className="active-tool-spinner">●</span>
          <span className="active-tool-label">
            running <span className="tool-chip">{activeTool.toolName}</span>
          </span>
          {activeTool.argsPreview && (
            <span className="active-tool-args" title={activeTool.argsPreview}>
              {activeTool.argsPreview}
            </span>
          )}
        </div>
      )}

      <div className="chat-bubbles">
        {bubbles.length === 0 && queuedBubbles.length === 0 && (
          <div className="empty">
            {props.isTerminal
              ? 'no narration captured for this run'
              : 'waiting for the agent’s first turn…'}
          </div>
        )}
        {bubbles.map((b) => (
          <div className="chat-bubble" key={`agent-${b.turn}`}>
            <div className="chat-bubble-meta mono">
              <span className="chat-bubble-turn">turn {b.turn}</span>
              {b.tools.length > 0 && (
                <span className="chat-bubble-tools">
                  {b.tools.map((t, i) => (
                    <span key={i} className="tool-chip">{t}</span>
                  ))}
                </span>
              )}
              <span className="chat-bubble-ts">{b.ts}</span>
            </div>
            {b.textPreview ? (
              <div className="chat-bubble-text">{b.textPreview}</div>
            ) : (
              <div className="chat-bubble-text dim">
                <em>(no narration this turn — tool call only)</em>
              </div>
            )}
          </div>
        ))}
        {queuedBubbles.map((q, i) => (
          <div
            className={`chat-bubble chat-bubble-queued ${q.result === 'failed' ? 'failed' : ''}`}
            key={`queued-${i}-${q.ts}`}
          >
            <div className="chat-bubble-meta mono">
              <span className="chat-bubble-turn">queued{q.by ? ` · ${q.by}` : ''}</span>
              <span className="chat-bubble-ts">{q.ts}</span>
              {q.result === 'failed' && <span className="tool-chip">failed</span>}
            </div>
            <div className="chat-bubble-text">
              {q.text}
              {q.result === 'failed' && q.reason && (
                <div className="dim"><em>{q.reason}</em></div>
              )}
            </div>
          </div>
        ))}
      </div>

      <form className="chat-composer" onSubmit={onSubmit}>
        <input
          className="chat-composer-input mono"
          placeholder={composePlaceholder}
          value={composeText}
          onChange={(e) => setComposeText(e.target.value)}
          disabled={!props.canInterject || composeStatus.kind === 'sending'}
          aria-label="queue a message for the agent"
          maxLength={INTERJECT_MAX_BYTES}
        />
        <button
          className="chat-composer-send"
          type="submit"
          disabled={!canSubmit}
          title={
            props.canInterject
              ? 'queue this message for the agent'
              : props.isTerminal
                ? 'run is terminal'
                : 'queue message is only supported for pi-managed runs'
          }
        >
          {composeStatus.kind === 'sending' ? 'queueing…' : 'queue'}
        </button>
      </form>
      {composeStatus.kind === 'error' && (
        <div className="chat-composer-error mono">queue failed — {composeStatus.reason}</div>
      )}
    </div>
  );
}

function EventsTab(props: {
  events: TimedEvent[];
  types: string[];
  muted: Set<string>;
  onToggleMute: (t: string) => void;
  connected: boolean;
}) {
  return (
    <div className="ev">
      <div className="ev-filter">
        <span className={`conn-dot ${props.connected ? '' : 'disconnected'}`} />
        <span className="ev-filter-label">mute:</span>
        {props.types.map((t) => (
          <button
            key={t}
            className={`filter-chip ${props.muted.has(t) ? 'muted' : ''}`}
            onClick={() => props.onToggleMute(t)}
          >
            {t}
          </button>
        ))}
      </div>
      <div className="ev-list">
        {props.events.length === 0 && <div className="empty">no events yet</div>}
        {props.events.map((e, i) => (
          <div className="ev-row" key={i}>
            <span className="ev-ts">{e.ts}</span>
            <span className={`event-type event-${e.type}`}>{e.type}</span>
            <span className="ev-text">{e.text}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function DiffTab({ run }: { run: RunVM }) {
  const { config } = useConfig();
  const [diff, setDiff] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [missing, setMissing] = useState(false);

  const prNumber = run.pr?.number ?? null;

  useEffect(() => {
    if (!prNumber) {
      setDiff(null);
      setMissing(true);
      setError(null);
      return;
    }
    setMissing(false);
    setError(null);
    setLoading(true);
    const ctrl = new AbortController();
    void api.getDiff(run.runId, ctrl.signal)
      .then((text) => {
        setDiff(text);
        setLoading(false);
      })
      .catch((err) => {
        if (ctrl.signal.aborted) return;
        if (err instanceof ApiError && err.status === 404) {
          setMissing(true);
          setLoading(false);
          return;
        }
        setError(err instanceof Error ? err.message : String(err));
        setLoading(false);
      });
    return () => ctrl.abort();
  }, [run.runId, prNumber]);

  // Prefer the run's own repo coordinates (stamped at run create time from
  // ticket.repo.url or the platform default). Falling back to the platform
  // default — what we used to do unconditionally — silently mis-links PRs
  // on multi-repo deployments: a run that opened PR #17 on repo-B gets a
  // link to default-repo/pull/17, which usually resolves to an unrelated
  // old PR. The fallback path is kept for legacy pre-multirepo rows whose
  // columns are NULL; they're guaranteed to belong to the platform default.
  const owner = run.githubOwner ?? config?.github?.owner;
  const repo = run.githubRepo ?? config?.github?.repo;
  const prUrl = owner && repo && prNumber
    ? `https://github.com/${owner}/${repo}/pull/${prNumber}`
    : null;

  if (!prNumber || missing) {
    return (
      <div className="diff-stub">
        <p>no PR yet — agent hasn't opened one.</p>
        <p className="dim">diff inspection becomes available once a PR is created.</p>
      </div>
    );
  }

  return (
    <div className="diff-tab">
      <div className="diff-head">
        <span className="mono dim">PR #{prNumber}</span>
        {prUrl && (
          <a href={prUrl} target="_blank" rel="noreferrer noopener" className="mono">
            open on GitHub ↗
          </a>
        )}
      </div>
      {loading && <div className="empty">loading diff…</div>}
      {error && <div className="empty">diff unavailable: {error}</div>}
      {!loading && !error && diff !== null && (
        <pre className="diff-body mono">{diff}</pre>
      )}
    </div>
  );
}

// Internal-review feedback shape, mirrors services/review-loop/src/types.ts
// ReviewIssue. Kept loose with `unknown` extraction because telemetry events
// are typed as ApiEventLine on the wire.
interface DrawerReviewIssue {
  file: string;
  line?: number;
  severity: 'error' | 'warning' | 'suggestion';
  category: string;
  message: string;
}

interface DrawerReviewLoop {
  ts: string;
  loopNumber: number | null;
  status: 'approved' | 'changes_requested' | 'escalate' | 'unknown';
  summary: string;
  issues: DrawerReviewIssue[];
}

function parseReviewCompleted(e: TimedEvent): DrawerReviewLoop | null {
  if (e.raw['event'] !== 'review_completed') return null;
  const rawIssues = e.raw['issues'];
  const issues: DrawerReviewIssue[] = Array.isArray(rawIssues)
    ? rawIssues.map((raw) => {
        const r = raw as Record<string, unknown>;
        return {
          file: typeof r['file'] === 'string' ? r['file'] : '(unknown)',
          ...(typeof r['line'] === 'number' ? { line: r['line'] } : {}),
          severity: (['error', 'warning', 'suggestion'].includes(String(r['severity']))
            ? r['severity']
            : 'suggestion') as DrawerReviewIssue['severity'],
          category: typeof r['category'] === 'string' ? r['category'] : 'general',
          message: typeof r['message'] === 'string' ? r['message'] : '',
        };
      })
    : [];
  const status = e.raw['status'];
  return {
    ts: e.ts,
    loopNumber: typeof e.raw['loop'] === 'number' ? e.raw['loop'] : null,
    status: (['approved', 'changes_requested', 'escalate'].includes(String(status))
      ? status
      : 'unknown') as DrawerReviewLoop['status'],
    summary: typeof e.raw['summary'] === 'string' ? e.raw['summary'] : '',
    issues,
  };
}

function ReviewsTab({ events }: { events: TimedEvent[] }) {
  // Each iteration of the internal review loop emits one `review_completed`
  // event carrying the structured issues + summary the reviewing model
  // produced. Grouped by loop number so the operator sees the progression
  // (loop 1: 3 issues → loop 2: 1 issue → approved).
  const loops = events.map(parseReviewCompleted).filter((l): l is DrawerReviewLoop => l !== null);

  if (loops.length === 0) {
    return (
      <div className="empty">
        no reviews yet — the review-loop publishes feedback after the
        implementer completes its first pass and a PR has opened
      </div>
    );
  }

  return (
    <div className="rv">
      {loops.map((loop, i) => (
        <details key={i} open className="rv-loop">
          <summary>
            <span className="rv-loop-n">
              loop {loop.loopNumber ?? i + 1}
            </span>
            <span className={`rv-status rv-${loop.status}`}>{loop.status.replace('_', ' ')}</span>
            <span className="rv-issue-count">
              {loop.issues.length} issue{loop.issues.length === 1 ? '' : 's'}
            </span>
            <span className="rv-loc mono">{loop.ts}</span>
          </summary>
          {loop.summary && <div className="rv-summary">{loop.summary}</div>}
          {loop.issues.length > 0 && (
            <ul className="rv-issues">
              {loop.issues.map((issue, k) => (
                <li key={k} className={`rv-issue rv-${issue.severity}`}>
                  <span className="rv-sev">{issue.severity}</span>
                  <span className="rv-loc mono">
                    {issue.file}{issue.line != null ? `:${issue.line}` : ''}
                  </span>
                  <span className="rv-category">{issue.category}</span>
                  <span className="rv-text">{issue.message}</span>
                </li>
              ))}
            </ul>
          )}
        </details>
      ))}
    </div>
  );
}

function CostTab({ run, events }: { run: RunVM; events: TimedEvent[] }) {
  // Sum cost from `cost_usd` fields on any event that carried them. The
  // strategies emit per-turn cost in their *_complete events; when those
  // aren't broken out, fall back to the run's authoritative cost_usd.
  const provider_calls = events.filter((e) => {
    const c = e.raw['cost_usd'];
    return typeof c === 'number' || (typeof c === 'string' && c.length > 0);
  });

  const phaseRows = [
    { name: 'implementing', ratio: 0.46 },
    { name: 'reviewing', ratio: 0.31 },
    { name: 'fixing', ratio: 0.18 },
    { name: 'auditing', ratio: 0.05 },
  ];

  return (
    <div className="cost">
      <div className="cost-total">
        <div className="cost-total-num">${run.cost.toFixed(2)}</div>
        <div className="cost-total-label">
          total spent · {run.loop.current}/{run.loop.max} loops
        </div>
      </div>
      <table className="cost-table">
        <thead>
          <tr>
            <th>phase</th>
            <th>cost</th>
            <th>turns</th>
          </tr>
        </thead>
        <tbody>
          {phaseRows.map((p) => (
            <tr key={p.name}>
              <td className="mono">{p.name}</td>
              <td className="mono">${(run.cost * p.ratio).toFixed(2)}</td>
              <td className="mono">—</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="dim mono" style={{ marginTop: 12, fontSize: 10.5 }}>
        cost breakdown is heuristic · {provider_calls.length} cost-bearing events
      </p>
    </div>
  );
}
